// Instrument panel for an instrument track: choose the sound source — the
// built-in subtractive synth (with editable ADSR/filter) or a sampled General
// MIDI program (all 128, three public soundfont banks). Edits apply live to the
// engine's instrument and persist on the track.
import { Store } from "../state/store";
import { AudioEngine } from "../audio/engine";
import { el } from "../util";
import { openModal, toast } from "./modal";
import { SYNTH_PRESETS, SYNTH_PRESET_LABELS, SynthParams } from "../audio/instrument";
import { GM_NAMES, GM_FAMILIES, SOUNDFONTS, DEFAULT_SOUNDFONT, gmDisplay } from "../audio/gm";
import { GM_DRUM_LABELS, DRUM_KITS } from "../audio/drums";
import { WAM_INSTRUMENTS } from "../audio/wam-presets";
import { TrackInstrument } from "../state/model";

const noteName = (m: number) => ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"][((m % 12) + 12) % 12] + (Math.floor(m / 12) - 1);

const PARAM_SPECS: { key: keyof SynthParams; label: string; min: number; max: number; step: number; unit?: string }[] = [
  { key: "attack", label: "Attack", min: 0, max: 2, step: 0.005, unit: "s" },
  { key: "decay", label: "Decay", min: 0, max: 2, step: 0.005, unit: "s" },
  { key: "sustain", label: "Sustain", min: 0, max: 1, step: 0.01 },
  { key: "release", label: "Release", min: 0.01, max: 3, step: 0.01, unit: "s" },
  { key: "cutoff", label: "Filter", min: 200, max: 12000, step: 50, unit: "Hz" },
  { key: "level", label: "Level", min: 0, max: 1, step: 0.01 },
];
const WAVES: OscillatorType[] = ["sawtooth", "square", "triangle", "sine"];

export function openInstrumentPanel(store: Store, engine: AudioEngine, trackId: string) {
  const track = store.track(trackId);
  if (!track) return;
  const body = el("div", { class: "fx-editor" });
  const row = (label: string, ctrl: HTMLElement) => el("div", { class: "fx-param" }, [el("label", { text: label }), ctrl]);
  const def = (): TrackInstrument => store.track(trackId)?.instrument ?? { type: "synth", preset: "lead" };
  const save = (d: TrackInstrument) => store.updateTrack(trackId, { instrument: d }, "tracks");
  const audition = (midi = 60) => { engine.resume(); const i = engine.instrument(trackId); if (i) { i.noteOn(midi, 0.9); setTimeout(() => i.noteOff(midi), 500); } };

  function render() {
    body.innerHTML = "";
    const d = def();
    // sound source
    const typeSel = el("select") as HTMLSelectElement;
    for (const [v, label] of [
      ["synth", "Built-in Synth"], ["gm", "General MIDI (128 instruments)"], ["drums", "Drum Kit"],
      ["sampler", "Sampler (your own samples)"], ["soundfont", "Soundfont (custom)"], ["wam", "WAM Instrument plugin"],
    ] as [string, string][]) {
      const o = el("option", { text: label, attrs: { value: v } }) as HTMLOptionElement;
      if (d.type === v) o.selected = true;
      typeSel.append(o);
    }
    typeSel.addEventListener("change", () => {
      const v = typeSel.value;
      const nd: TrackInstrument =
        v === "gm" ? { type: "gm", program: 0, soundfont: DEFAULT_SOUNDFONT } :
        v === "drums" ? { type: "drums" } :
        v === "sampler" ? { type: "sampler", mode: "pitched", rootMidi: 60, samples: [] } :
        v === "soundfont" ? { type: "soundfont", name: "acoustic_grand_piano", soundfont: DEFAULT_SOUNDFONT } :
        v === "wam" ? { type: "wam", url: "" } :
        { type: "synth", preset: "lead" };
      save(nd); engine.rebuildInstrument(trackId); render();
    });
    body.append(row("Source", typeSel));

    if (d.type === "gm") renderGM(d);
    else if (d.type === "drums") renderDrums();
    else if (d.type === "sampler") renderSampler(d);
    else if (d.type === "soundfont") renderSoundfont(d);
    else if (d.type === "wam") renderWam(d);
    else renderSynth(d);

    body.append(el("p", { class: "hint", text: "Play with the 🎹 Keys toggle (musical typing) or a MIDI keyboard. Record-arm (R) this track to aim notes at it." }));
  }

  function renderGM(d: Extract<TrackInstrument, { type: "gm" }>) {
    const bankSel = el("select") as HTMLSelectElement;
    for (const sf of SOUNDFONTS) { const o = el("option", { text: sf.replace(/_/g, " "), attrs: { value: sf } }) as HTMLOptionElement; if (sf === (d.soundfont || DEFAULT_SOUNDFONT)) o.selected = true; bankSel.append(o); }

    const instSel = el("select") as HTMLSelectElement;
    for (let fam = 0; fam < 16; fam++) {
      const grp = document.createElement("optgroup"); grp.label = GM_FAMILIES[fam];
      for (let p = fam * 8; p < fam * 8 + 8; p++) {
        const o = el("option", { text: `${p + 1}. ${gmDisplay(GM_NAMES[p])}`, attrs: { value: String(p) } }) as HTMLOptionElement;
        if (p === d.program) o.selected = true;
        grp.append(o);
      }
      instSel.append(grp);
    }

    const status = el("span", { class: "badge", text: "loading…" });
    const reflect = () => {
      const cur: any = engine.instrument(trackId);
      if (cur?.loaded) { status.textContent = "ready"; status.className = "badge live"; }
      else { status.textContent = "loading…"; status.className = "badge"; (cur?.ready ?? Promise.resolve()).then(() => { status.textContent = "ready"; status.className = "badge live"; }); }
    };
    const apply = async () => {
      save({ type: "gm", program: Number(instSel.value), soundfont: bankSel.value });
      status.textContent = "loading…"; status.className = "badge";
      await engine.rebuildInstrument(trackId);
      status.textContent = "ready"; status.className = "badge live";
      audition();
    };
    bankSel.addEventListener("change", apply);
    instSel.addEventListener("change", apply);

    body.append(
      row("Bank", bankSel),
      row("Instrument", instSel),
      el("div", { class: "clip-actions" }, [actionBtn("▶ Audition", () => audition()), status]),
      el("p", { class: "hint", text: "Sampled instruments stream from a public soundfont CDN (cached after first use)." }),
    );
    reflect();
  }

  function renderDrums() {
    const d = def();
    const curKit = (d.type === "drums" && d.kit) || "acoustic";
    const kitSel = el("select") as HTMLSelectElement;
    for (const k of Object.keys(DRUM_KITS)) { const o = el("option", { text: DRUM_KITS[k].label, attrs: { value: k } }) as HTMLOptionElement; if (k === curKit) o.selected = true; kitSel.append(o); }
    kitSel.addEventListener("change", async () => { save({ type: "drums", kit: kitSel.value }); await engine.rebuildInstrument(trackId); audition(36); });
    body.append(row("Kit", kitSel));
    body.append(el("p", { class: "hint", text: "Synthesized drum machine. In the piano roll each row is a drum (GM percussion map). Click a pad to preview:" }));
    const pads = el("div", { class: "clip-actions" });
    for (const midi of [36, 38, 42, 46, 45, 49, 51, 39, 37, 56]) {
      pads.append(actionBtn(GM_DRUM_LABELS[midi] ?? String(midi), () => audition(midi)));
    }
    body.append(pads);
  }

  // GM percussion notes commonly used when auto-assigning drum pads.
  const KIT_NOTES = [36, 38, 42, 46, 45, 50, 49, 51, 39, 37, 56, 54, 75, 70, 41, 43];

  function renderSampler(d: Extract<TrackInstrument, { type: "sampler" }>) {
    const modeSel = el("select") as HTMLSelectElement;
    for (const [v, label] of [["pitched", "Pitched (melodic — one sample across the keys)"], ["kit", "Drum pads (one-shot per note)"]] as [string, string][]) {
      const o = el("option", { text: label, attrs: { value: v } }) as HTMLOptionElement; if (v === d.mode) o.selected = true; modeSel.append(o);
    }
    modeSel.addEventListener("change", async () => { save({ type: "sampler", mode: modeSel.value as any, rootMidi: d.rootMidi ?? 60, samples: d.samples }); await engine.rebuildInstrument(trackId); render(); });
    body.append(row("Mode", modeSel));

    if (d.mode === "pitched") {
      const rootSel = el("select") as HTMLSelectElement;
      for (let m = 24; m <= 96; m++) { const o = el("option", { text: noteName(m), attrs: { value: String(m) } }) as HTMLOptionElement; if (m === (d.rootMidi ?? 60)) o.selected = true; rootSel.append(o); }
      rootSel.addEventListener("change", async () => { save({ type: "sampler", mode: "pitched", rootMidi: Number(rootSel.value), samples: d.samples }); await engine.rebuildInstrument(trackId); });
      body.append(row("Root note", rootSel));
    }

    const fileIn = el("input", { attrs: { type: "file", accept: "audio/*", multiple: "true" } }) as HTMLInputElement;
    fileIn.style.display = "none";
    fileIn.addEventListener("change", async () => {
      const files = Array.from(fileIn.files || []); fileIn.value = "";
      if (!files.length) return;
      const samples = [...d.samples];
      for (const f of files) {
        try {
          const buf = await engine.ctx.decodeAudioData(await f.arrayBuffer());
          const asset = store.addAsset(buf, f.name);
          const note = d.mode === "kit" ? (KIT_NOTES[samples.length] ?? 36 + samples.length) : undefined;
          samples.push({ assetId: asset.id, name: f.name, note });
        } catch { toast(`Couldn't decode ${f.name}.`, 5000); }
      }
      save({ type: "sampler", mode: d.mode, rootMidi: d.rootMidi ?? 60, samples });
      await engine.rebuildInstrument(trackId);
      render();
    });
    body.append(el("div", { class: "clip-actions" }, [actionBtn("＋ Load samples…", () => fileIn.click()), fileIn]));

    if (!d.samples.length) body.append(el("p", { class: "hint", text: d.mode === "kit" ? "Load one-shots (kick, snare, hats…) — each is auto-mapped to a GM drum note." : "Load a WAV/MP3 sample; it plays across the keyboard, pitch-shifted from the root note." }));
    d.samples.forEach((s, idx) => {
      const bits: HTMLElement[] = [el("span", { class: "hint", text: s.name })];
      if (d.mode === "kit") {
        const nsel = el("select") as HTMLSelectElement;
        for (const m of KIT_NOTES) { const o = el("option", { text: GM_DRUM_LABELS[m] ?? String(m), attrs: { value: String(m) } }) as HTMLOptionElement; if (m === s.note) o.selected = true; nsel.append(o); }
        nsel.addEventListener("change", async () => { const samples = d.samples.map((x, i) => i === idx ? { ...x, note: Number(nsel.value) } : x); save({ type: "sampler", mode: "kit", samples }); await engine.rebuildInstrument(trackId); });
        bits.push(nsel);
      }
      bits.push(actionBtn("▶", () => audition(d.mode === "kit" ? (s.note ?? 36) : (d.rootMidi ?? 60))));
      bits.push(actionBtn("✕", async () => { const samples = d.samples.filter((_, i) => i !== idx); save({ type: "sampler", mode: d.mode, rootMidi: d.rootMidi, samples }); await engine.rebuildInstrument(trackId); render(); }));
      body.append(el("div", { class: "clip-actions" }, bits));
    });
    body.append(el("p", { class: "hint", text: "Samples are saved with the project (browser quick-save)." }));
  }

  function renderSoundfont(d: Extract<TrackInstrument, { type: "soundfont" }>) {
    const bankSel = el("select") as HTMLSelectElement;
    for (const sf of SOUNDFONTS) { const o = el("option", { text: sf.replace(/_/g, " "), attrs: { value: sf } }) as HTMLOptionElement; if (sf === (d.soundfont || DEFAULT_SOUNDFONT)) o.selected = true; bankSel.append(o); }
    // Full grouped instrument list (not a datalist — a datalist filters its
    // suggestions to the current value, so a pre-filled field looks "stuck").
    const nameSel = el("select") as HTMLSelectElement;
    for (let fam = 0; fam < 16; fam++) {
      const grp = document.createElement("optgroup"); grp.label = GM_FAMILIES[fam];
      for (let p = fam * 8; p < fam * 8 + 8; p++) {
        const o = el("option", { text: `${p + 1}. ${gmDisplay(GM_NAMES[p])}`, attrs: { value: GM_NAMES[p] } }) as HTMLOptionElement;
        if (GM_NAMES[p] === (d.name || "acoustic_grand_piano")) o.selected = true;
        grp.append(o);
      }
      nameSel.append(grp);
    }
    const urlIn = el("input", { attrs: { value: d.url || "", placeholder: "…or a soundfont-player module URL (optional)" } }) as HTMLInputElement;
    urlIn.style.minWidth = "260px";
    const status = el("span", { class: "badge", text: "—" });
    const apply = async () => {
      save({ type: "soundfont", name: nameSel.value, soundfont: bankSel.value, url: urlIn.value.trim() || undefined });
      status.textContent = "loading…"; status.className = "badge";
      await engine.rebuildInstrument(trackId);
      const ok = (engine.instrument(trackId) as any)?.loaded;
      status.textContent = ok ? "ready" : "failed"; status.className = ok ? "badge live" : "badge";
      if (ok) audition();
    };
    bankSel.addEventListener("change", apply);
    nameSel.addEventListener("change", apply);
    urlIn.addEventListener("change", apply);
    body.append(row("Bank", bankSel), row("Instrument", nameSel), row("URL", urlIn), el("div", { class: "clip-actions" }, [actionBtn("Load", apply), actionBtn("▶ Audition", () => audition()), status]),
      el("p", { class: "hint", text: "Pick any instrument from the bank, or paste a URL to a soundfont-player (MIDI.js) module (the URL overrides the picker)." }));
  }

  function renderWam(d: Extract<TrackInstrument, { type: "wam" }>) {
    const urlIn = el("input", { attrs: { value: d.url || "", placeholder: "https://…/index.js (WAM v2 instrument module)" } }) as HTMLInputElement;
    urlIn.style.minWidth = "300px";
    const status = el("span", { class: "badge", text: d.url ? "—" : "no plugin" });
    const load = async () => {
      const url = urlIn.value.trim(); if (!url) return;
      save({ type: "wam", url });
      status.textContent = "loading…"; status.className = "badge";
      await engine.rebuildInstrument(trackId);
      const ok = !!(engine.instrument(trackId) as any)?.instance;
      status.textContent = ok ? "ready" : "failed"; status.className = ok ? "badge live" : "badge";
    };
    const openGui = async () => {
      const inst: any = engine.instrument(trackId);
      let gui: HTMLElement | null = null;
      try { gui = await inst?.instance?.createGui?.(); } catch {}
      const b = el("div", {}, gui ? [gui] : [el("p", { class: "hint", text: "This plugin has no GUI (or hasn't loaded)." })]);
      openModal(inst?.name || "WAM Instrument", b, [{ label: "Close", primary: true, onClick: () => { try { inst?.instance?.destroyGui?.(gui); } catch {} } }]);
    };
    // curated built-in picks (verified WAM v2 instruments)
    const presetSel = el("select") as HTMLSelectElement;
    presetSel.append(el("option", { text: "— choose a verified plugin —", attrs: { value: "" } }));
    for (const p of WAM_INSTRUMENTS) { const o = el("option", { text: p.name, attrs: { value: p.url } }) as HTMLOptionElement; if (p.url === d.url) o.selected = true; presetSel.append(o); }
    presetSel.addEventListener("change", () => { if (presetSel.value) { urlIn.value = presetSel.value; load(); } });

    body.append(row("Preset", presetSel), row("Plugin URL", urlIn), el("div", { class: "clip-actions" }, [actionBtn("Load", load), actionBtn("Open GUI", openGui), actionBtn("▶ Audition", () => audition()), status]),
      el("p", { class: "hint", text: "Pick a verified plugin or paste any WAM v2 instrument URL. Loads from the plugin author's server; not included in offline exports (like WAM effects)." }));
  }

  function renderSynth(d: Extract<TrackInstrument, { type: "synth" }>) {
    const cur: SynthParams = { ...(SYNTH_PRESETS[d.preset] ?? SYNTH_PRESETS.lead), ...(d.params as any) };
    const synth = () => engine.instrument(trackId) as any;
    const applyParam = (key: keyof SynthParams, val: number | string) => {
      const cd = def();
      const nd: TrackInstrument = cd.type === "synth" ? { ...cd, params: { ...(cd.params ?? {}), [key]: val } } : { type: "synth", preset: d.preset, params: { [key]: val } };
      save(nd); synth()?.setParams?.({ [key]: val } as any);
    };

    const presetSel = el("select") as HTMLSelectElement;
    for (const k of Object.keys(SYNTH_PRESETS)) { const o = el("option", { text: SYNTH_PRESET_LABELS[k] ?? k, attrs: { value: k } }) as HTMLOptionElement; if (k === d.preset) o.selected = true; presetSel.append(o); }
    presetSel.addEventListener("change", () => { save({ type: "synth", preset: presetSel.value }); synth()?.setPreset?.(presetSel.value); render(); });
    body.append(row("Preset", presetSel));

    const waveSel = el("select") as HTMLSelectElement;
    for (const w of WAVES) { const o = el("option", { text: w, attrs: { value: w } }) as HTMLOptionElement; if (w === cur.wave) o.selected = true; waveSel.append(o); }
    waveSel.addEventListener("change", () => applyParam("wave", waveSel.value));
    body.append(row("Wave", waveSel));

    for (const spec of PARAM_SPECS) {
      const v = Number(cur[spec.key]);
      const range = el("input", { attrs: { type: "range", min: String(spec.min), max: String(spec.max), step: String(spec.step), value: String(v) } }) as HTMLInputElement;
      const out = el("output", { text: `${v}${spec.unit ?? ""}` });
      range.addEventListener("input", () => { const n = Number(range.value); out.textContent = `${n}${spec.unit ?? ""}`; applyParam(spec.key, n); });
      body.append(el("div", { class: "fx-param" }, [el("label", { text: spec.label }), range, out]));
    }
  }

  render();
  openModal(`Instrument — ${track.name}`, body, [{ label: "Done", primary: true, onClick: () => {} }]);
}

function actionBtn(label: string, on: () => void): HTMLButtonElement {
  const b = el("button", { text: label }); b.addEventListener("click", on); return b;
}
